'use client';

import { useEffect, useRef, useState } from 'react';

import {
  checkCpfRegistrationStatus,
  getCustomerGuest,
  verifyCustomerGuestByCpf,
} from '@/actions';
import { useDebounce } from '@/hooks';
import { toast, useForm, zodResolver, type Control, type FieldErrors } from '@/libs';
import { useHumanPass } from '@/providers/HumanPassProvider';
import { useCheckoutStore } from '@/stores';
import {
  CpfRegistrationStatus,
  userLoginSchema,
  type UserLoginFormData,
} from '@/types/models';
import { formatErrorMessage } from '@/types/models/ApiError';
import { isHumanPassDenied, logger } from '@/utils';

import type { RegistrationSeed } from './useUserRegisterForm';

import { CUSTOMER_GUEST_MESSAGES } from '../constants';

type UserData = {
  name: string;
  socialName?: string;
  document: string;
  phone: string;
  customerId: string;
  hasAccount: boolean;
  hasMail: boolean;
  hasAddress: boolean;
};

type LoginSuccessOptions = { skipCheckoutRedirect?: boolean };

type UseUserLoginFormProps = {
  onLoginSuccess: (data: UserData, options?: LoginSuccessOptions) => void;
  onNeedsRegistration: (seed: RegistrationSeed) => void;
};

type UseUserLoginFormReturn = {
  control: Control<UserLoginFormData>;
  errors: FieldErrors<UserLoginFormData>;
  isLoading: boolean;
  isCheckingCpf: boolean;
  cpfRegistrationStatus: CpfRegistrationStatus | null;
  cpfStatusError: string | null;
  isCreateMode: boolean;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  getSubmitLoadingText: () => string;
};

const CPF_FORMAT_REGEX = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;

export const useUserLoginForm = ({
  onLoginSuccess,
  onNeedsRegistration,
}: UseUserLoginFormProps): UseUserLoginFormReturn => {
  const utmData = useCheckoutStore((state) => state.checkoutData.utmData);

  // O widget vive no HumanPassProvider, global. Daqui sai só a execução das
  // consultas protegidas -- esperar o passe e repetir após re-desafio é
  // responsabilidade do provider, não de cada formulário.
  const { runWithPass } = useHumanPass();

  const [isCheckingCpf, setIsCheckingCpf] = useState(false);
  const [cpfRegistrationStatus, setCpfRegistrationStatus] =
    useState<CpfRegistrationStatus | null>(null);
  const [cpfStatusError, setCpfStatusError] = useState<string | null>(null);
  const lastCheckedCpfRef = useRef<string | null>(null);

  // Mantido em ref de propósito: incluir a função nas dependências do efeito
  // faria a consulta de CPF re-disparar se a identidade dela mudasse.
  const runWithPassRef = useRef(runWithPass);
  runWithPassRef.current = runWithPass;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UserLoginFormData>({
    resolver: zodResolver(userLoginSchema),
    defaultValues: {
      document: '',
      phone: '',
    },
  });

  const documentValue = watch('document');
  const debouncedDocument = useDebounce(documentValue, 500);

  useEffect(() => {
    if (!CPF_FORMAT_REGEX.test(debouncedDocument ?? '')) {
      setCpfRegistrationStatus(null);
      setCpfStatusError(null);
      lastCheckedCpfRef.current = null;
      return;
    }

    const cleanDocument = debouncedDocument.replace(/\D/g, '');
    if (lastCheckedCpfRef.current === cleanDocument) {
      return;
    }

    let active = true;
    lastCheckedCpfRef.current = cleanDocument;
    setIsCheckingCpf(true);
    setCpfStatusError(null);

    const failStatusCheck = (reason: string) => {
      setCpfRegistrationStatus(null);
      setCpfStatusError(CUSTOMER_GUEST_MESSAGES.STATUS_CHECK_FAILED);
      lastCheckedCpfRef.current = null;
      logger.error(CUSTOMER_GUEST_MESSAGES.LOGIN_ERROR, { error: reason });
    };

    runWithPassRef
      .current(() => checkCpfRegistrationStatus(cleanDocument))
      .then((result) => {
        if (!active) return;

        if (result.success) {
          setCpfRegistrationStatus(result.status);
          return;
        }

        if (result.code === 'human-pass-required') {
          // Chegar aqui significa que nem a segunda tentativa passou: o
          // provider já esperou o passe e já repetiu depois do re-desafio.
          //
          // Antes este ramo limpava o estado em silêncio e não reconsultava --
          // o efeito depende de `debouncedDocument`, que não mudou. O campo
          // ficava sem resultado e sem mensagem, e o usuário não tinha o que
          // fazer. Agora mostra o motivo e libera o mesmo CPF para nova
          // tentativa.
          setCpfRegistrationStatus(null);
          setCpfStatusError(result.error);
          lastCheckedCpfRef.current = null;
          return;
        }

        failStatusCheck(result.error);
      })
      .catch((error) => {
        if (active) {
          failStatusCheck(error instanceof Error ? error.message : 'Unknown error');
        }
      })
      .finally(() => {
        if (active) setIsCheckingCpf(false);
      });

    return () => {
      active = false;
    };
  }, [debouncedDocument]);

  const handleCreateAccount = async (data: UserLoginFormData) => {
    const cleanDocument = data.document.replace(/\D/g, '');
    const created = await runWithPass(() =>
      verifyCustomerGuestByCpf(cleanDocument, utmData || undefined),
    );

    if (isHumanPassDenied(created)) {
      toast.error(created.error);
      return;
    }

    if (!created) {
      logger.error(CUSTOMER_GUEST_MESSAGES.LOGIN_ERROR, {
        error: 'Account creation returned no data',
      });
      toast.error(CUSTOMER_GUEST_MESSAGES.DEFAULT_ERROR);
      return;
    }

    if (created.hasAccount) {
      onLoginSuccess(
        {
          name: created.name || '',
          socialName: '',
          document: data.document,
          phone: data.phone,
          customerId: created.customerId || '',
          hasAccount: true,
          hasMail: created.hasMail,
          hasAddress: created.hasAddress,
        },
        { skipCheckoutRedirect: true },
      );
      return;
    }

    onNeedsRegistration({
      document: data.document,
      phone: data.phone,
      customerId: created.customerId || '',
      name: created.name || '',
      hasMail: created.hasMail,
      hasAddress: created.hasAddress,
    });
  };

  const handleAccessAccount = async (data: UserLoginFormData) => {
    const cleanDocument = data.document.replace(/\D/g, '');
    const cleanPhone = data.phone;

    const customerGuest = await runWithPass(() =>
      getCustomerGuest(cleanDocument, cleanPhone, utmData || undefined),
    );

    if (isHumanPassDenied(customerGuest)) {
      toast.error(customerGuest.error);
      return;
    }

    if (customerGuest) {
      onLoginSuccess({
        name: customerGuest.name,
        socialName: customerGuest.socialName,
        document: data.document,
        phone: data.phone,
        customerId: customerGuest.customerId,
        hasAccount: customerGuest.hasAccount,
        hasMail: customerGuest.hasMail,
        hasAddress: customerGuest.hasAddress,
      });
      toast.success(CUSTOMER_GUEST_MESSAGES.LOGIN_SUCCESS);
      return;
    }

    logger.info(CUSTOMER_GUEST_MESSAGES.CPF_NOT_FOUND);
    onLoginSuccess({
      name: '',
      socialName: '',
      document: data.document,
      phone: data.phone,
      customerId: '',
      hasAccount: false,
      hasMail: false,
      hasAddress: false,
    });
  };

  const isCreateMode =
    cpfRegistrationStatus === CpfRegistrationStatus.NotFound ||
    cpfRegistrationStatus === CpfRegistrationStatus.Incomplete;

  const onSubmit = async (data: UserLoginFormData) => {
    if (isCreateMode) {
      try {
        await handleCreateAccount(data);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.error(CUSTOMER_GUEST_MESSAGES.LOGIN_ERROR, { error: errorMessage });
        toast.error(formatErrorMessage(error, CUSTOMER_GUEST_MESSAGES.DEFAULT_ERROR));
      }
      return;
    }

    try {
      await handleAccessAccount(data);
    } catch (error) {
      const isPhoneMismatch =
        error instanceof Error &&
        error.message === CUSTOMER_GUEST_MESSAGES.PHONE_MISMATCH;

      if (isPhoneMismatch) {
        try {
          const cleanDocument = data.document.replace(/\D/g, '');
          const partialUser = await runWithPass(() =>
            verifyCustomerGuestByCpf(cleanDocument, utmData || undefined),
          );

          if (isHumanPassDenied(partialUser)) {
            toast.error(partialUser.error);
            return;
          }

          if (partialUser && !partialUser.hasAccount) {
            onLoginSuccess({
              name: partialUser.name || '',
              socialName: '',
              document: data.document,
              phone: data.phone,
              customerId: partialUser.customerId || '',
              hasAccount: false,
              hasMail: partialUser.hasMail,
              hasAddress: partialUser.hasAddress,
            });
            return;
          }
        } catch {
          // fallback to generic error below
        }
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error(CUSTOMER_GUEST_MESSAGES.LOGIN_ERROR, { error: errorMessage });
      toast.error(formatErrorMessage(error, CUSTOMER_GUEST_MESSAGES.DEFAULT_ERROR));
    }
  };

  const getSubmitLoadingText = () => {
    if (isCheckingCpf) return 'Validando...';
    if (isCreateMode) return 'Criando...';
    return 'Acessando...';
  };

  return {
    control,
    errors,
    isLoading: isSubmitting,
    isCheckingCpf,
    cpfRegistrationStatus,
    cpfStatusError,
    isCreateMode,
    handleSubmit: handleSubmit(onSubmit),
    getSubmitLoadingText,
  };
};
